/**
 * Shape and kind-parsing for workflow run / node lifecycle PubSub messages.
 *
 * Owned by Workflows rather than by `AzureWebPubSubUserContext` so the socket
 * layer stays a transport: it imports the allowed-kind set to decide what to
 * queue, and every run-state consumer parses kinds through the same function.
 */
import { WORKFLOW_EVENT_TYPE } from 'src/types/enum';

export type WorkflowExecutionPubSubPayload = Record<string, unknown>;

/**
 * A run event plus a monotonic sequence number. Run events are queued rather
 * than held in a single state slot: two PubSub messages landing in the same
 * React batch would otherwise coalesce, silently dropping the earlier one and
 * leaving its node stuck on "In progress" after the run finished. Consumers
 * track the last `seq` they handled instead of the last payload identity.
 */
export type WorkflowExecutionQueuedEvent = {
  seq: number;
  payload: WorkflowExecutionPubSubPayload;
};

/** Normalized kinds the socket layer forwards to run-state consumers. */
export const WORKFLOW_RUN_EVENT_KINDS = new Set(
  Object.values(WORKFLOW_EVENT_TYPE).map((v) => v.toLowerCase())
);

/**
 * Normalized run-event kind off either `event_type` or `event`, case- and
 * separator-insensitive — backends send both `NODE_COMPLETED` and
 * `node-completed`. Returns `''` when the payload carries neither.
 */
export function workflowRunEventKind(data: unknown): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const { event_type: eventType, event } = data as {
    event_type?: unknown;
    event?: unknown;
  };
  const raw =
    typeof eventType === 'string' && eventType.trim() !== ''
      ? eventType
      : event;
  if (typeof raw !== 'string') {
    return '';
  }
  return raw.trim().toLowerCase().replaceAll('-', '_');
}
